import React, { useState, useEffect, useCallback } from 'react';
import './BuildHistory.css';

const REFRESH_MS = 10000;

// TeamCity timestamps look like "20260601T143005+0000". Parse best-effort; fall
// back to the raw string so a format change never blanks the row.
function parseTcDate(s) {
  if (!s || typeof s !== 'string') return null;
  const m = s.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})([+-]\d{4})?$/);
  if (!m) return null;
  const [, y, mo, d, h, mi, sec, tz] = m;
  const offset = tz ? `${tz.slice(0, 3)}:${tz.slice(3)}` : '';
  const dt = new Date(`${y}-${mo}-${d}T${h}:${mi}:${sec}${offset}`);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function formatTime(s) {
  const dt = parseTcDate(s);
  return dt ? dt.toLocaleString() : (s || '');
}

function statusClass(build) {
  if (build.state === 'queued') return 'queued';
  if (build.state === 'running') return 'running';
  return build.status === 'SUCCESS' ? 'success' : 'failure';
}

function BuildHistory({ teamcityApi, onRerun, onBack }) {
  const [data, setData] = useState({ queued: [], running: [], finished: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const result = await teamcityApi.getMyBuilds();
      setData({
        queued: result.queued || [],
        running: result.running || [],
        finished: result.finished || [],
      });
      setError(null);
    } catch (err) {
      setError(`Failed to load your builds${err.message ? `: ${err.message}` : ''}`);
    } finally {
      setLoading(false);
    }
  }, [teamcityApi]);

  // Initial load + 10s poll, matching BuildServers' cadence.
  useEffect(() => {
    load();
    const interval = setInterval(load, REFRESH_MS);
    return () => clearInterval(interval);
  }, [load]);

  async function handleCancel(build) {
    const verb = build.state === 'queued' ? 'cancel' : 'stop';
    const label = `${build.buildTypeName || build.buildTypeId} #${build.number}`;
    if (!window.confirm(`Are you sure you want to ${verb} ${label}?`)) return;
    try {
      await teamcityApi.cancelBuild(build.id, { queued: build.state === 'queued' });
      load();
    } catch (err) {
      setError(`Failed to cancel build${err.message ? `: ${err.message}` : ''}`);
    }
  }

  function renderRow(build) {
    const cancellable = build.state === 'queued' || build.state === 'running';
    return (
      <div key={build.id} className={`history-row ${statusClass(build)}`}>
        <div className="history-row-main">
          <span className="history-build-name">{build.buildTypeName || build.buildTypeId}</span>
          <span className="history-build-number">#{build.number}</span>
          <span className={`history-status ${statusClass(build)}`}>
            {build.statusText || build.status || build.state}
          </span>
        </div>
        <div className="history-row-meta">
          {build.rerun?.baseCL && (
            <span className="history-meta-item">base CL {build.rerun.baseCL}</span>
          )}
          {build.rerun?.shelvedChangelist && (
            <span className="history-meta-item">shelved {build.rerun.shelvedChangelist}</span>
          )}
          {build.agent?.name && <span className="history-meta-item">{build.agent.name}</span>}
          {(build.startDate || build.queuedDate) && (
            <span className="history-meta-item">{formatTime(build.startDate || build.queuedDate)}</span>
          )}
          {build.waitReason && <span className="history-meta-item">{build.waitReason}</span>}
        </div>
        <div className="history-row-actions">
          <button type="button" className="rerun-button" onClick={() => onRerun(build.rerun)}>
            Re-run
          </button>
          {cancellable && (
            <button type="button" className="cancel-button" onClick={() => handleCancel(build)}>
              {build.state === 'queued' ? 'Cancel' : 'Stop'}
            </button>
          )}
          {build.webUrl && (
            <a
              className="history-tc-link"
              href={build.webUrl}
              target="_blank"
              rel="noopener noreferrer"
              title="View in TeamCity"
            >
              🔗
            </a>
          )}
        </div>
      </div>
    );
  }

  const total = data.queued.length + data.running.length + data.finished.length;

  return (
    <div className="build-history">
      <div className="build-history-header">
        <button onClick={onBack} className="back-button">
          &larr; Back to Dashboard
        </button>
        <h2>My Builds</h2>
      </div>

      {error && <div className="build-history-error">⚠ {error}</div>}

      {loading ? (
        <div className="loading">Loading...</div>
      ) : total === 0 && !error ? (
        <div className="build-history-empty">
          <p>No builds found. Builds you start from Run Build will show up here.</p>
        </div>
      ) : (
        <>
          {data.queued.length > 0 && (
            <section className="history-section">
              <h3>Queued ({data.queued.length})</h3>
              {data.queued.map(renderRow)}
            </section>
          )}
          {data.running.length > 0 && (
            <section className="history-section">
              <h3>Running ({data.running.length})</h3>
              {data.running.map(renderRow)}
            </section>
          )}
          {data.finished.length > 0 && (
            <section className="history-section">
              <h3>Recent ({data.finished.length})</h3>
              {data.finished.map(renderRow)}
            </section>
          )}
        </>
      )}
    </div>
  );
}

export default BuildHistory;
